import axios from "axios";
import { strings, paths } from "../utils/constants";
import { Toast } from "../component/Toast";

const getToken = (isAdmin) => {
  let tokenValue = "";
  if (isAdmin) {
    tokenValue = localStorage.getItem("adminToken");
  } else {
    tokenValue = localStorage.getItem("token");
  }
  return tokenValue;
};

const axiosApi = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT,
  timeout: 10000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

export const buildEndpoint = (callFor) => {
  return paths.endpoints[callFor];
};

const exceptionHandler = (e, isAdmin) => {
  if (e.code === strings.error.NETWORK_ERROR) {
    Toast({
      type: "error",
      title: e.message,
      message: e.message,
    });
    return e.message;
  }
  if (e.response.status === 401) {
    let errorData = {
      errors: [{ message: strings.error.NOT_AUTHORIZED_ERROR }],
    };
    Toast({
      type: "error",
      title: `${e.response.status} ${e.response.statusText}`,
      message: strings.error.NOT_AUTHORIZED_ERROR,
    });
    return errorData;
  } else {
    const errorMessage =
      e.response?.data?.errors?.[0]?.message ||
      e.response?.data?.message ||
      "Something went wrong. Please try again.";
    Toast({
      type: "error",
      title: `${e.response.status} ${e.response.statusText}`,
      message: errorMessage,
    });
    return e.response.data;
  }
};

export const getRequest = async (
  language = strings.language.EN,
  endpoint,
  isAdmin = false
) => {
  try {
    const token = getToken(isAdmin);
    const { data } = await axiosApi.get(language + buildEndpoint(endpoint), {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return data;
  } catch (e) {
    exceptionHandler(e, isAdmin);
  }
};

export const getRequestWithParams = async (
  language = strings.language.EN,
  endpoint,
  urlParams,
  isAdmin = false
) => {
  const token = getToken(isAdmin);
  try {
    const endPoint = buildEndpoint(endpoint);
    const requestUrl = language + endPoint + urlParams;
    const { data } = await axiosApi.get(requestUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return data;
  } catch (e) {
    exceptionHandler(e, isAdmin);
  }
};

export const postRequest = async (
  language = strings.language.EN,
  endpoint,
  body,
  isAdmin = false,
  customTost = false
) => {
  const token = getToken(isAdmin);
  try {
    const { data } = await axiosApi.post(
      language + buildEndpoint(endpoint),
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (!customTost) {
      Toast({
        type: "success",
        title: strings.words.SUCCESS,
        message: data.message,
      });
    }
    return data;
  } catch (e) {
    exceptionHandler(e, isAdmin);
  }
};

export const postRequestForFormData = async (
  language = strings.language.EN,
  endpoint,
  body,
  isAdmin = false,
  customTost = false
) => {
  const token = getToken(isAdmin);
  try {
    const { data } = await axiosApi.post(
      language + buildEndpoint(endpoint),
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    if (!customTost) {
      Toast({
        type: "success",
        title: strings.words.SUCCESS,
        message: data.message,
      });
    }
    return data;
  } catch (e) {
    exceptionHandler(e, isAdmin);
  }
};

export const putRequest = async (
  language = strings.language.EN,
  endpoint,
  body,
  isAdmin = false
) => {
  const token = getToken(isAdmin);
  try {
    const { data } = await axiosApi.put(
      language + buildEndpoint(endpoint),
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return data;
  } catch (e) {
    exceptionHandler(e, isAdmin);
  }
};

export const patchRequestForFormData = async (
  language = strings.language.EN,
  endpoint,
  body,
  isAdmin = false,
  customTost = false
) => {
  const token = getToken(isAdmin);
  try {
    const { data } = await axiosApi.patch(
      language + buildEndpoint(endpoint),
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    if (!customTost) {
      Toast({
        type: "success",
        title: strings.words.SUCCESS,
        message: data.message,
      });
    }
    return data;
  } catch (e) {
    exceptionHandler(e, isAdmin);
  }
};

export const patchRequest = async (
  language = strings.language.EN,
  endpoint,
  body,
  isAdmin = false,
  customTost = false
) => {
  const token = getToken(isAdmin);
  try {
    const { data } = await axiosApi.patch(
      language + buildEndpoint(endpoint),
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (!customTost) {
      Toast({
        type: "success",
        title: strings.words.SUCCESS,
        message: data.message,
      });
    }
    return data;
  } catch (e) {
    exceptionHandler(e, isAdmin);
  }
};

export const deleteRequest = async (
  language = strings.language.EN,
  endpoint,
  body,
  isAdmin = false,
  customSuccessMessage = null
) => {
  const token = getToken(isAdmin);
  try {
    const endPoint = buildEndpoint(endpoint);

    const requestUrl = language + endPoint + "/" + body;

    const { data } = await axiosApi.delete(requestUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    Toast({
      type: "success",
      title: strings.words.SUCCESS,
      message: customSuccessMessage || data.message,
    });
    return data;
  } catch (e) {
    exceptionHandler(e, isAdmin);
  }
};

export const deleteById = async (
  language = strings.language.EN,
  endpoint,
  body,
  isAdmin = false
) => {
  const token = getToken(isAdmin);
  try {
    const endPoint = buildEndpoint(endpoint);
    const requestUrl = language + endPoint;

    const { data } = await axiosApi.delete(requestUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      data: { id: body },
    });
    Toast({
      type: "success",
      title: strings.words.SUCCESS,
      message: data.message,
    });
    return data;
  } catch (e) {
    exceptionHandler(e, isAdmin);
  }
};

// API Key based requests for public endpoints
export const getRequestWithApiKey = async (
  language = strings.language.EN,
  endpoint
) => {
  try {
    const { data } = await axiosApi.get(language + buildEndpoint(endpoint), {
      headers: {
        "X-API-KEY": process.env.REACT_APP_API_KEY,
        "Content-Type": "application/json",
      },
    });
    return data;
  } catch (e) {
    // Handle errors without token-based logic
    if (e.code === strings.error.NETWORK_ERROR) {
      Toast({
        type: "error",
        title: e.message,
        message: e.message,
      });
      return e.message;
    }
    if (e.response?.status === 401) {
      let errorData = {
        errors: [{ message: strings.error.NOT_AUTHORIZED_ERROR }],
      };
      Toast({
        type: "error",
        title: `${e.response.status} ${e.response.statusText}`,
        message: strings.error.NOT_AUTHORIZED_ERROR,
      });
      return errorData;
    } else {
      Toast({
        type: "error",
        title: `${e.response.status} ${e.response.statusText}`,
        message: e.response?.data?.errors?.[0]?.message || "An error occurred",
      });
      return e.response?.data;
    }
  }
};

export const getRequestWithParamsAndApiKey = async (
  language = strings.language.EN,
  endpoint,
  urlParams
) => {
  try {
    const endPoint = buildEndpoint(endpoint);
    const requestUrl = language + endPoint + urlParams;
    const { data } = await axiosApi.get(requestUrl, {
      headers: {
        "X-API-KEY": process.env.REACT_APP_API_KEY,
        "Content-Type": "application/json",
      },
    });
    return data;
  } catch (e) {
    // Handle errors without token-based logic
    if (e.code === strings.error.NETWORK_ERROR) {
      Toast({
        type: "error",
        title: e.message,
        message: e.message,
      });
      return e.message;
    }
    if (e.response?.status === 401) {
      let errorData = {
        errors: [{ message: strings.error.NOT_AUTHORIZED_ERROR }],
      };
      Toast({
        type: "error",
        title: `${e.response.status} ${e.response.statusText}`,
        message: strings.error.NOT_AUTHORIZED_ERROR,
      });
      return errorData;
    } else {
      Toast({
        type: "error",
        title: `${e.response.status} ${e.response.statusText}`,
        message: e.response?.data?.errors?.[0]?.message || "An error occurred",
      });
      return e.response?.data;
    }
  }
};

export const postRequestWithApiKey = async (
  language = strings.language.EN,
  endpoint,
  body,
  customTost = false
) => {
  try {
    const { data } = await axiosApi.post(
      language + buildEndpoint(endpoint),
      body,
      {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
      }
    );
    if (!customTost) {
      Toast({
        type: "success",
        title: strings.words.SUCCESS,
        message: data.message,
      });
    }
    return data;
  } catch (e) {
    // Handle errors without token-based logic
    if (e.code === strings.error.NETWORK_ERROR) {
      Toast({
        type: "error",
        title: e.message,
        message: e.message,
      });
      return e.message;
    }
    if (e.response?.status === 401) {
      let errorData = {
        errors: [{ message: strings.error.NOT_AUTHORIZED_ERROR }],
      };
      Toast({
        type: "error",
        title: `${e.response.status} ${e.response.statusText}`,
        message: strings.error.NOT_AUTHORIZED_ERROR,
      });
      return errorData;
    } else {
      Toast({
        type: "error",
        title: `${e.response.status} ${e.response.statusText}`,
        message: e.response?.data?.errors?.[0]?.message || "An error occurred",
      });
      return e.response?.data;
    }
  }
};

export { getToken };
